<script lang="ts" setup>
import { computed, onMounted, onBeforeUnmount } from 'vue'

import IconClose from '@lahaus-roomie/roomie/src/assets/icons/close.svg'
import RoomieOverlay from '@lahaus-roomie/roomie/src/components/RoomieOverlay/index.vue'

import type { Props } from './types'
import AppChatMigratedBubbles from '@/components/App/ChatV2/Migrated/Bubbles/index.vue'
import UserIdentityContainer from '@/components/App/UserIdentityContainer/index.vue'

const { t } = useI18n()
const { $useLhId } = useNuxtApp()
const { public: { samConfiguration } } = useRuntimeConfig()
const googleStaticMapApiKey = samConfiguration.googleStaticMapApiKey

const props = defineProps<Props>()

onMounted(() => {
  document.body.style.overflow = 'hidden'
})

onBeforeUnmount(() => {
  document.body.style.overflow = 'auto'
})

const emit = defineEmits(['closeChat', 'error'])

const listingId = computed(() => props.listing.code)

</script>

<script lang="ts">
export default {
  name: 'AppChatWindow'
}
</script>

<template>
  <RoomieOverlay
    class="app-chat__window-overlay cursor-pointer"
    @overlay-closed="emit('closeChat')">
    <LazyAppModal
      v-if="showForm"
      class="lock-overlay__registration-modal !fixed top-0 z-60"
      :close-tracking-lh-id="$useLhId({ P: screen, Z: 'chat window', A: 'cerrar modal', C: 'cerrar_icon' })"
      @close="emit('closeChat')">
      <UserIdentityContainer
        :id="`${screen}-user-identity-container`"
        :listing-id
        :screen
        :data-attributes="{ dataLh: {P: screen, Z: 'chat window'}}"
        :should-enqueue-twilio-contact="listing.ims?.external_status === 'active'" />
    </LazyAppModal>

    <section
      v-else
      class="app-chat__window-holder">
      <div class="app-chat__window md:border border-carbon-light-300 md:rounded-8 overflow-hidden flex flex-col justify-between">
        <header class="px-24 py-16 bg-white flex items-center gap-22 relative app-chat__shallow-header">
          <div class="w-48 h-48 rounded-full relative app-chat__shallow-picture">
            <img
              :src="botAgentImage"
              class="w-full h-full object-cover"
              :alt="botAgentName">

            <div class="w-16 h-16 bg-spring-600 border-2 border-white rounded-full absolute -bottom-1 -right-2" />
          </div>

          <div>
            <p class="text-18 text-carbon-800 font-semibold">
              {{ botAgentName }}
            </p>

            <span
              class="text-14 text-carbon-600 block font-normal">
              {{ t('status') }}
            </span>
          </div>

          <button
            id="app-chat-close-button"
            v-lh-id="{P: screen, Z: 'chat window', A: 'cerrar ventana chat', C: t('closeCta')}"
            :aria-label="t('closeCta')"
            type="button"
            class="w-32 h-32 m-8 absolute right-0 top-0"
            @click="emit('closeChat')">
            <IconClose class="fill-primary-600 pointer-events-none" />
          </button>
        </header>

        <AppChatMigratedBubbles
          :listing
          :bot-agent-name
          :screen
          :google-static-map-api-key="googleStaticMapApiKey"
          :face-to-face-visit-enabled
          @error="emit('error')" />
      </div>
    </section>
  </RoomieOverlay>
</template>

<style lang="scss">
.lh-roomie-v2 {
  .app-chat {
    &__window-overlay {
      backdrop-filter: none;
      background-color: #0000004d;
    }

    &__window-holder {
      @apply z-60 flex flex-col;
      @apply w-full h-full md:w-auto md:h-auto;
      @apply fixed bottom-0 right-0;
    }

    &__window {
      @apply shadow-[0_16px_24px_2px_#485b5733];

      background-color: var(--color-gray-400);
      height: 100vh;
      width: 100%;

      @media(min-width: theme('screens.md')) {
        height: calc(100vh - 60px);
        width: 480px;
      }
    }

    &__shallow-picture {
      @apply shadow-[0_8px_16px_2px_#485b5733];
    }

    &__shallow-header {
      @apply shadow-[0_15px_24px_2px_#485b571a];

      button {
        svg {
          path {
            @apply fill-pine-600;
          }
        }
      }
    }

    .contact-form {
      form {
        @apply mt-12
      }
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json" />
